import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

import useAuthStore from '@entities/auth/model/useAuthStore';
import { DOCUMENT_STATE } from '@entities/approval/constants';
import ApprovalLineTable from '@features/document-write/components/ApprovalLineTable/ApprovalLineTable';
import AttachmentSection from '@features/document-write/components/AttachmentSection/AttachmentSection';
import CKEditorViewer from '@features/document-write/components/CKEditorViewer/CKEditorViewer';
import CommentSection from '@features/document-write/components/CommentSection/CommentSection';
import DocumentInfoForm from '@features/document-write/components/DocumentInfoForm/DocumentInfoForm';
import DocumentPreview from '@features/document-write/components/DocumentPreview/DocumentPreview';
import {
  useDocumentCommentListQuery,
  useDocumentExecutorListQuery,
  useDocumentFileListQuery,
  useDocumentReceiverListQuery,
  useDocumentReferenceDocsQuery,
} from '@features/document-write/hooks';
import ReferenceDocumentSection from '@features/document-write/components/ReferenceDocumentSection/ReferenceDocumentSection';
import SummaryNoteSection from '@features/document-write/components/SummaryNoteSection/SummaryNoteSection';
import { useApprovalGuideModal } from '@app/providers/ApprovalGuideModalProvider';
import Alert from '@shared/components/atoms/Alert';
import Button from '@shared/components/atoms/Button';
import Confirm from '@shared/components/atoms/Confirm';
import LoadingOverlay from '@shared/components/atoms/LoadingOverlay/LoadingOverlay';

import CommentDetailModal from './CommentDetailModal';
import InputModal from '@shared/components/atoms/InputModal/InputModal';
import Modal from '@shared/components/atoms/Modal/Modal';
import useAlert from '@shared/hooks/useAlert';
import useConfirm from '@shared/hooks/useConfirm';
import questionIcon from '@shared/assets/images/question_icon.svg';
import searchMdIcon from '@shared/assets/images/search-md_icon.svg';

import { useDocumentDetail, useEmployeeQueries, useDocumentActions } from './hooks';
import { transformDocumentInfo, transformReferenceDocuments, transformAttachments } from './utils';

import styles from './DocumentDetailModal.module.scss';

/**
 * DocumentDetailModal 컴포넌트
 * - 품의서 상세 모달 (읽기 전용)
 * - 결재자가 열면 수정 기능 없음
 * - 에디터 툴바 없이 표시
 * - 피그마 디자인 기반: node-id=776-4619
 *
 * 리팩토링:
 * - useDocumentDetail: 문서 로딩 로직
 * - useEmployeeQueries: 직원 정보 조회 로직
 * - useDocumentActions: 액션 핸들러 로직
 * - utils/transformDocumentData: 데이터 변환 유틸리티
 */
function DocumentDetailModal({
  isOpen,
  onClose,
  docIdx,
  userRole = 'approver',
  tabType,
  isNested = false,
  isAdminContext = false,
  onSaveSuccess,
}) {
  const user = useAuthStore((state) => state.user);
  const enumber = useAuthStore((state) => state.enumber); // enumber는 별도 필드
  const companyCode = useAuthStore((state) => {
    const code = state.companyCode;
    return code ? Number(code) : null;
  });

  // 역할 및 탭 확인
  const isApprover = userRole === 'approver';
  const isApprovalIngTab = tabType === 'approvalIng';
  const isDrafter = userRole === 'drafter';

  // 결재 라인 안내 모달
  const { isOpen: isGuideOpen, open: openApprovalGuideModal } = useApprovalGuideModal();

  // 문서 상세 로딩
  const { isLoading, docData, error } = useDocumentDetail(docIdx, isOpen, userRole);

  // 문서 로딩 에러 전용 Alert (액션 Alert과 분리)
  const {
    alertState: loadErrorAlertState,
    showAlert: showLoadErrorAlert,
    closeAlert: closeLoadErrorAlert,
  } = useAlert();
  const {
    confirmState: adminDeleteConfirmState,
    showConfirm: showAdminDeleteConfirm,
    closeConfirm: closeAdminDeleteConfirm,
  } = useConfirm();

  function getLoadErrorMessage(rawError) {
    const message = typeof rawError === 'string' ? rawError : '';

    // CRA dev proxy 에러 문구 사용자 노출 방지
    if (message.includes('Error occurred while trying to proxy')) {
      return '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.';
    }

    if (message.includes('유효하지 않은 문서 인덱스')) {
      return message;
    }

    return '문서를 불러오는 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
  }

  useEffect(
    function () {
      if (!isOpen || !error) {
        return;
      }

      showLoadErrorAlert(getLoadErrorMessage(error), function () {
        onClose();
      });
    },
    [error, isOpen, onClose, showLoadErrorAlert],
  );

  // 문서 상태 확인 (API에서 문자열로 올 수 있으므로 Number로 비교)
  const documentState = docData?.state != null ? Number(docData.state) : undefined;
  const isReturned = documentState === DOCUMENT_STATE.RETURN;

  // 수정요청: 문서 전체 state가 6이거나, 결재라인 중 하나라도 state가 6인 경우
  // - 수정요청은 결재자(예: sign_2, sign_3)가 하므로 sign_1_state가 아닌 다른 라인이 6일 수 있음
  const approvalLineStates = docData
    ? [
        docData.sign_1_state,
        docData.sign_2_state,
        docData.sign_3_state,
        docData.sign_4_state,
        docData.sign_5_state,
        docData.check_1_state,
        docData.check_2_state,
        docData.check_3_state,
        docData.check_4_state,
        docData.agreement_state,
      ].filter((s) => s != null)
    : [];
  const hasModificationRequestInLine = approvalLineStates.some(
    (s) => Number(s) === DOCUMENT_STATE.RETURNEDIT,
  );
  const isModificationRequested =
    documentState === DOCUMENT_STATE.RETURNEDIT || hasModificationRequestInLine;

  // 진행중(결재중)·수정요청 상태의 결재문서는 관리자도 삭제 불가
  // 문서관리 목록의 선택삭제(handleDeleteSelected) 기준과 동일하게 적용
  // 결재대기/결재완료/반려 문서는 삭제 허용
  const isApprovalInProgress =
    documentState === DOCUMENT_STATE.SIGNING || documentState === DOCUMENT_STATE.RETURNEDIT;

  // 로그인한 사용자가 문서의 기안자인지 확인 (스토어의 enumber 사용 - user 객체 안에 없을 수 있음)
  const isDocumentOwner = useMemo(
    function () {
      if (!docData || (enumber == null && user?.enumber == null)) {
        return false;
      }
      const drafterEnumber = Number(docData.sign_1);
      const currentUserEnumber = Number(enumber ?? user?.enumber);
      return drafterEnumber === currentUserEnumber;
    },
    [docData?.sign_1, enumber, user?.enumber],
  );

  // 수정/삭제 버튼: 상신한 사용자(기안자)만 표시
  // - 수정요청은 결재자가 하고, 수정은 로그인한 기안자 본인이 해야 함
  // - 기안자 본인이고 문서가 반려 또는 수정요청 상태일 때만 표시 (어디서 열었는지와 무관)
  const showModifyDeleteButtons = isDocumentOwner && (isReturned || isModificationRequested);

  // 결재 상태 확인: state === 3이 결재완료
  // 참조자/시행자 수정 가능 여부: 결재완료 + 기안자 본인
  const isEditable = useMemo(
    function () {
      if (!docData || isNested) {
        return false;
      }
      // 결재완료 문서인지 확인
      if (docData.state !== 3) {
        return false;
      }
      // 기안자 본인인지 확인
      const drafterEnumber = Number(docData.sign_1);
      const currentEnumber = Number(enumber);
      return drafterEnumber === currentEnumber;
    },
    [docData?.state, docData?.sign_1, enumber, isNested],
  );

  // 문서 결재종료 여부 (코멘트 수정/삭제 가능 여부 판단용)
  // 결재완료(3), 반려(4), 수정요청(6) - 결재 프로세스가 끝난 상태에서 코멘트 수정/삭제 가능
  // API 응답이 문자열로 올 수 있어 Number()로 비교
  const isDocumentSigned = useMemo(
    function () {
      const state = Number(docData?.state);
      return (
        state === DOCUMENT_STATE.SIGNED ||
        state === DOCUMENT_STATE.RETURN ||
        state === DOCUMENT_STATE.RETURNEDIT
      );
    },
    [docData?.state],
  );

  // 기안일자 수정 가능 여부 (어드민 전용 + 결재완료 상태)
  const canEditDraftDate = isAdminContext && documentState === 3 && !isNested;

  // 참조자/시행자/참조문서/첨부파일/코멘트 조회
  const { data: receiverListData } = useDocumentReceiverListQuery(docIdx, !!isOpen);
  const { data: executorListData } = useDocumentExecutorListQuery(docIdx, !!isOpen);
  const { data: referenceDocListData } = useDocumentReferenceDocsQuery(docIdx, !!isOpen);
  const { data: fileListData } = useDocumentFileListQuery(docIdx, !!isOpen);
  const { data: commentListData } = useDocumentCommentListQuery(docIdx, !!isOpen);

  // 직원 정보 조회
  const { employeeMap, recipientEmployeeMap, executorEmployeeMap } = useEmployeeQueries({
    docData,
    companyCode,
    receiverListData,
    executorListData,
  });

  // 문서 정보 변환
  const documentInfo = useMemo(
    function () {
      return transformDocumentInfo(
        docData,
        employeeMap,
        receiverListData,
        executorListData,
        recipientEmployeeMap,
        executorEmployeeMap,
      );
    },
    [
      docData,
      employeeMap,
      receiverListData,
      executorListData,
      recipientEmployeeMap,
      executorEmployeeMap,
    ],
  );

  // 참조문서 변환
  const referenceDocuments = useMemo(
    function () {
      return transformReferenceDocuments(referenceDocListData);
    },
    [referenceDocListData],
  );

  // 첨부파일 변환
  const attachments = useMemo(
    function () {
      return transformAttachments(fileListData);
    },
    [fileListData],
  );

  // 액션 핸들러
  const {
    alertState,
    isInputModalOpen,
    inputModalValue,
    isRejectModalOpen,
    rejectModalValue,
    isApproveConfirmOpen,
    isCommentModalOpen,
    commentModalValue,
    isCommentDetailModalOpen,
    selectedComment,
    isCommentEditModalOpen,
    commentEditValue,
    stackedDocIds,
    setInputModalValue,
    setRejectModalValue,
    setCommentModalValue,
    setCommentEditValue,
    showAlert,
    closeAlert,
    handleDocumentInfoChange,
    handleFileDownload,
    isPreviewMode,
    printContentRef,
    handlePreview,
    handleClosePreview,
    handlePrint,
    handleRequestModification,
    handleInputModalConfirm,
    handleInputModalCancel,
    handleReject,
    handleRejectModalConfirm,
    handleRejectModalCancel,
    handleApprove,
    handleApproveConfirm,
    handleApproveCancel,
    handleCommentWrite,
    handleCommentModalConfirm,
    handleCommentModalCancel,
    handleCommentClick,
    handleCommentDetailClose,
    handleCommentModify,
    handleCommentEditConfirm,
    handleCommentEditCancel,
    handleCommentDelete,
    handleModify,
    handleRewrite,
    handleDelete,
    handleReferenceClick,
    handleStackedClose,
    calculatedTotalPages,
    editorContentByPage,
    previewImageData,
    isDownloading,
    isCapturing,
    editedDraftDate,
    handleDraftDateChange,
    handleDraftDateSave,
    isSavingDraftDate,
    finalDocumentInfo,
    handleAdminDelete,
  } = useDocumentActions({
    docIdx,
    docData,
    documentInfo,
    referenceDocuments,
    attachments,
    onClose,
    onSaveSuccess,
  });

  // 코멘트 소유자 여부 (useDocumentActions 이후, early return 전에 훅 호출 필요)
  const isCommentOwner = useMemo(
    function () {
      // 본인이 작성한 코멘트인지 확인
      if (selectedComment && enumber) {
        const commentEnumber = selectedComment.user || selectedComment.enumber;
        const commentEnumberNum = Number(commentEnumber);
        const userEnumberNum = Number(enumber);
        const isMyComment = commentEnumberNum === userEnumberNum;

        if (!isMyComment) return false;

        // 수정/삭제 가능 조건:
        // 1. 문서 상태가 결재완료(state === 3)인 경우 (모바일과 동일, 기안자 여부 무관)
        // 2. 또는 결재자 페이지의 미결함(tabType === 'approvalIng') 탭에서 내가 결재할 차례인 경우
        return isDocumentSigned || (isApprover && isApprovalIngTab && !isNested);
      }

      return false;
    },
    [selectedComment, enumber, isDocumentSigned, isApprover, isApprovalIngTab, isNested],
  );

  /**
   * [createPortal 사용 이유]
   *
   * 이슈 상황:
   * - DocumentDetailModal은 다른 페이지 컴포넌트(DrafterPage 등) 안에서 렌더링됨
   * - isPreviewMode가 true일 때 DocumentPreview를 반환하지만,
   *   부모 컴포넌트의 DOM 계층 구조 안에서 렌더링되어 제대로 표시되지 않음
   * - 결과적으로 모달이 닫힌 것처럼 보이고 미리보기 화면이 나타나지 않음
   *
   * 해결 방법:
   * - createPortal을 사용하여 DocumentPreview를 document.body에 직접 렌더링
   * - 부모 컴포넌트의 DOM 계층 구조를 벗어나 전체 화면을 차지하도록 함
   *
   * createPortal 동작 원리:
   * - React의 createPortal(children, container)는 children을 container DOM 노드에 렌더링
   * - 컴포넌트의 논리적 위치(React 트리)는 유지하면서, 물리적 위치(DOM 트리)만 변경
   * - 이벤트 버블링은 React 트리를 따라 전파되므로, 부모 컴포넌트에서 이벤트 처리 가능
   * - 주로 모달, 툴팁, 드롭다운 등 부모 요소의 overflow나 z-index 제약을 벗어나야 할 때 사용
   */
  if (isPreviewMode) {
    return createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: '#f4f6fa',
          overflow: 'auto',
        }}
      >
        {/*
          DocumentPreview 컴포넌트에 ref 전달
          - forwardRef로 래핑된 컴포넌트이므로 ref prop으로 전달
          - react-to-print가 이 ref를 통해 인쇄할 DOM 요소를 찾음
        */}
        <DocumentPreview
          ref={printContentRef}
          title={docData?.title || '품의서'}
          documentInfo={finalDocumentInfo}
          editorContent={docData?.contents || ''}
          summaryNote={docData?.summary || docData?.note || docData?.summary_note || ''}
          attachments={attachments}
          referenceDocuments={referenceDocuments}
          useCorporateSeal={
            docData?.is_corp_seal_used === 1 ||
            docData?.is_corp_seal_used === '1' ||
            docData?.isCorpSealUsed === 1 ||
            docData?.isCorpSealUsed === '1'
          }
          onClose={handleClosePreview}
          onPrint={handlePrint}
          docIdx={docIdx}
          tabType={tabType}
          editorContentByPage={editorContentByPage}
          initialTotalPages={calculatedTotalPages}
          editorImageUrl={previewImageData?.url}
          editorImageSlices={previewImageData?.slices}
        />
        <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
        <Alert
          isOpen={loadErrorAlertState.isOpen}
          message={loadErrorAlertState.message}
          onClose={closeLoadErrorAlert}
        />
      </div>,
      document.body,
    );
  }

  return (
    <>
      {/* 문서 로딩 에러 Alert이 열려있을 땐 Modal을 렌더링하지 않음 (불필요한 회색 영역 제거) */}
      {isOpen && !loadErrorAlertState.isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size="a4"
          className={styles.documentDetailModal}
          closeOnOverlayClick={false}
          showCloseButton={false}
          classes={{
            modalBody: styles.documentDetailModalBody, // 문서상세 전용: overflow-y auto 적용 안 되게
            modalContent: styles.documentDetailModalContent,
          }}
        >
          {/* 로딩 상태 */}
          {isLoading && (
            <div className={styles.loadingContainer}>
              <div className={styles.circularProgress} role="status" aria-label="문서 로딩 중" />
              <div className={styles.loadingMessage}>문서를 불러오는 중...</div>
            </div>
          )}

          {/* 문서 내용 */}
          {!isLoading && !error && docData && documentInfo && (
            <div
              className={`${styles.documentContent} ${isInputModalOpen || isRejectModalOpen || isApproveConfirmOpen || isCommentModalOpen || isCommentEditModalOpen || isDownloading || isCapturing || isGuideOpen ? styles.disabled : ''}`}
            >
              {(isDownloading || isCapturing) && (
                <LoadingOverlay
                  message={isCapturing ? '미리보기를 준비하고 있습니다...' : '파일 다운로드 중...'}
                />
              )}
              {/* 고정 헤더: 닫기 버튼 */}
              <div className={styles.fixedHeader}>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={onClose}
                  aria-label="닫기"
                >
                  ×
                </button>
              </div>

              {/* 스크롤 영역: 제목, 본문, 참조문서, 코멘트 */}
              <div className={styles.scrollArea}>
                <div className={styles.contentContainer}>
                  {/* 헤더 */}
                  <div className={styles.modalHeader}>
                    <div className={styles.headerTitle}>
                      <h2 className={styles.title}>{docData.title || '품의서'}</h2>
                      <button
                        type="button"
                        className={styles.helpButton}
                        onClick={() => openApprovalGuideModal(docData?.title)}
                      >
                        <img src={questionIcon} alt="도움말" className={styles.helpIcon} />
                        <span className={styles.helpText}>결제 라인 안내</span>
                      </button>
                    </div>
                  </div>

                  {/* 상단: 문서 정보 + 결재 라인 */}
                  <div className={styles.topSection}>
                    <div className={styles.documentInfoTop}>
                      <DocumentInfoForm
                        documentInfo={finalDocumentInfo}
                        onDocumentInfoChange={handleDraftDateChange}
                        isPreview={true}
                        isDraftDateEditable={canEditDraftDate}
                        section="top"
                      />
                    </div>
                    <div className={styles.approvalLineWrapper}>
                      <ApprovalLineTable
                        documentInfo={finalDocumentInfo}
                        onDocumentInfoChange={function () {}}
                        isPreview={true}
                        docIdx={docIdx}
                        tabType={tabType}
                      />
                    </div>
                  </div>

                  {/* 인감 날인·인감증명서 사용(법인인감/사용인감) 체크박스 */}
                  <div className={styles.corporateSealCheckbox}>
                    <input
                      type="checkbox"
                      checked={
                        docData.is_corp_seal_used === 1 ||
                        docData.is_corp_seal_used === '1' ||
                        docData.isCorpSealUsed === 1 ||
                        docData.isCorpSealUsed === '1'
                      }
                      readOnly
                      disabled
                    />
                    <label>인감 날인·인감증명서 사용(법인인감/사용인감)</label>
                  </div>

                  {/* 중간: 참조자/시행자 + 제목 */}
                  <div className={styles.middleSection}>
                    <DocumentInfoForm
                      documentInfo={finalDocumentInfo}
                      onDocumentInfoChange={handleDocumentInfoChange}
                      isPreview={true}
                      isEditable={isEditable}
                      isDraftDateEditable={canEditDraftDate}
                      section="bottom"
                    />
                  </div>

                  {/* 본문 에디터 (읽기 전용) */}
                  <div className={styles.editorSection}>
                    <CKEditorViewer value={docData.contents || ''} />
                  </div>

                  {/* 요약 및 비고 + 첨부파일 */}
                  <div className={styles.summaryAndAttachmentContainer}>
                    <SummaryNoteSection
                      summaryNote={docData.summary || docData.note || docData.summary_note || ''}
                      isPreview={true}
                    />
                    <div className={styles.divider} />
                    <AttachmentSection
                      attachments={attachments}
                      isPreview={true}
                      onFileClick={handleFileDownload}
                    />
                  </div>

                  {/* 하단: 참조문서 + 코멘트 */}
                  <div className={styles.bottomSection}>
                    <div className={styles.referenceDocsWrapper}>
                      <ReferenceDocumentSection
                        referenceDocuments={referenceDocuments}
                        onReferenceDocumentsChange={function () {}}
                        isPreview={true}
                        onDocumentClick={handleReferenceClick}
                      />
                    </div>
                    <div className={styles.commentWrapper}>
                      <CommentSection
                        comments={commentListData || []}
                        isPreview={true}
                        showWriteButton={isApprover && isApprovalIngTab && !isNested}
                        onWriteClick={handleCommentWrite}
                        onCommentClick={handleCommentClick}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* 고정 푸터: 미리보기/결재/반려 등 */}
              <div className={styles.footerButtons}>
                {/* 미리보기 버튼 */}
                <div className={styles.buttonGroup}>
                  {/* 문서상태가 삭제(5)가 아니고, 진행중(2)·수정요청(6) 상태가 아닐 때만 삭제버튼 노출 */}
                  {isAdminContext && !isNested && docData?.state !== 5 && !isApprovalInProgress && (
                    <Button
                      variant="danger"
                      onClick={() =>
                        showAdminDeleteConfirm('문서를 삭제하시겠습니까?', handleAdminDelete)
                      }
                      className={styles.deleteButton}
                    >
                      삭제
                    </Button>
                  )}
                  <Button
                    variant="default"
                    onClick={handlePreview}
                    className={styles.previewButton}
                  >
                    <img src={searchMdIcon} alt="" className={styles.previewIcon} />
                    <span>미리보기</span>
                  </Button>
                </div>

                {/* 결재자 - 미결함 탭: 결재/반려/수정요청 버튼 - 중첩 모달(참조문서)에서는 표시하지 않음 */}
                {isApprover && isApprovalIngTab && !isNested && (
                  <div className={styles.rightButtons}>
                    <Button variant="default" onClick={handleRequestModification}>
                      수정요청
                    </Button>
                    <Button variant="default" onClick={handleReject}>
                      반려
                    </Button>
                    <Button variant="primary" onClick={handleApprove}>
                      결재
                    </Button>
                  </div>
                )}

                {/* 기안자 - 반려/수정요청 문서: 수정/재작성 버튼 표시 (기안자는 삭제 권한 없음) - 중첩 모달에서는 표시하지 않음 */}
                {showModifyDeleteButtons && !isNested && (
                  <div className={styles.rightButtons}>
                    {isModificationRequested && (
                      <Button variant="default" onClick={handleModify}>
                        수정
                      </Button>
                    )}
                    {isReturned && (
                      <Button variant="default" onClick={handleRewrite}>
                        재작성
                      </Button>
                    )}
                  </div>
                )}

                {/* 기안일자 저장 버튼 (어드민 전용) - 변경사항이 있을 때만 노출 */}
                {canEditDraftDate && editedDraftDate && (
                  <div className={styles.rightButtons}>
                    <Button
                      variant="primary"
                      onClick={handleDraftDateSave}
                      disabled={isSavingDraftDate}
                    >
                      {isSavingDraftDate ? '저장 중...' : '저장'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* 액션 Alert (Modal 밖에서 단독 렌더링 - InputModal 위에 표시되도록) */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />

      {/* 문서 로딩 에러 Alert (Modal 밖에서 단독 렌더링) */}
      <Alert
        isOpen={loadErrorAlertState.isOpen}
        message={loadErrorAlertState.message}
        onClose={closeLoadErrorAlert}
      />

      {/* 수정요청 InputModal */}
      <InputModal
        isOpen={isInputModalOpen}
        title="수정요청 사유를 입력해주세요."
        value={inputModalValue}
        onChange={setInputModalValue}
        onConfirm={handleInputModalConfirm}
        onCancel={handleInputModalCancel}
        placeholder="수정요청 사유를 입력해주세요."
        onExceedMaxLength={() => showAlert('500자까지 입력 가능합니다.')}
      />

      {/* 반려 InputModal */}
      <InputModal
        isOpen={isRejectModalOpen}
        title="반려 사유를 입력해주세요."
        value={rejectModalValue}
        onChange={setRejectModalValue}
        onConfirm={handleRejectModalConfirm}
        onCancel={handleRejectModalCancel}
        placeholder="반려 사유를 입력해주세요."
        onExceedMaxLength={() => showAlert('500자까지 입력 가능합니다.')}
      />

      {/* 관리자 삭제 확인 모달 */}
      <Confirm
        isOpen={adminDeleteConfirmState.isOpen}
        message={adminDeleteConfirmState.message}
        onConfirm={() => {
          adminDeleteConfirmState.onConfirm?.();
          closeAdminDeleteConfirm();
        }}
        onCancel={closeAdminDeleteConfirm}
        confirmText="삭제"
        cancelText="취소"
      />

      {/* 결재 확인 모달 */}
      <Confirm
        isOpen={isApproveConfirmOpen}
        message="결재하시겠습니까?"
        onConfirm={handleApproveConfirm}
        onCancel={handleApproveCancel}
        confirmText="예"
        cancelText="아니오"
      />

      {/* 코멘트 작성 InputModal */}
      <InputModal
        isOpen={isCommentModalOpen}
        title="코멘트 내용을 입력해 주세요."
        value={commentModalValue}
        onChange={setCommentModalValue}
        onConfirm={handleCommentModalConfirm}
        onCancel={handleCommentModalCancel}
        placeholder="내용을 입력해주세요."
        onExceedMaxLength={() => showAlert('500자까지 입력 가능합니다.')}
      />

      {/* 코멘트 상세 모달 */}
      <CommentDetailModal
        isOpen={isCommentDetailModalOpen}
        comment={selectedComment}
        isOwner={isCommentOwner}
        onClose={handleCommentDetailClose}
        onModify={handleCommentModify}
        onDelete={handleCommentDelete}
      />

      {/* 코멘트 수정 InputModal */}
      <InputModal
        isOpen={isCommentEditModalOpen}
        title="코멘트 내용을 수정해 주세요."
        value={commentEditValue}
        onChange={setCommentEditValue}
        onConfirm={handleCommentEditConfirm}
        onCancel={handleCommentEditCancel}
        placeholder="내용을 입력해주세요."
        onExceedMaxLength={() => showAlert('500자까지 입력 가능합니다.')}
      />

      {/* 중첩된 모달 렌더링 */}
      {stackedDocIds.map(function (id, index) {
        return (
          <DocumentDetailModal
            key={`stacked-${index}-${id}`}
            isOpen={true}
            onClose={handleStackedClose}
            docIdx={id}
            userRole="viewer"
            tabType="reference"
            isNested={true}
          />
        );
      })}
    </>
  );
}

export default DocumentDetailModal;
